'use client';

/**
 * The owner's view of an agent's memory (webagents gap-closure plan item
 * 2.1, principle 7, 2026-09-26): the namespaces the SDK memory skill keeps on
 * the platform (the agent's own notes, the notes shared with every caller,
 * and one namespace per verified caller), with search, edit, forget, export
 * and retention per namespace. Mounted inside the Memory section of the
 * agent settings (`agent-details.tsx`); the data comes from
 * `app/api/agents/[id]/memory`.
 *
 * The section mounts this panel on its first expand, never while collapsed:
 * mounted with the page, it fetched `/memory` twice (the namespaces and the
 * first page of entries) for a section most owners never open (portal visual
 * pass, 2026-09-28, fixed by the final-portal-ui lane).
 *
 * RETENTION IS PER NAMESPACE (`memory_namespaces`). The two namespaces every
 * agent has, `owner` and `shared`, are listed even while empty, so a fresh
 * agent, whose server list is empty, still has something to set retention
 * on; with "Everything" picked the panel says how to pick one instead of
 * hiding the control without a word. A caller's namespace appears once that
 * caller has notes.
 */

import { useCallback, useEffect, useId, useState } from 'react';
import { Download, FileEdit, RefreshCw, Search, Trash2, Users, X } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { Spinner } from '@/components/ui/spinner';

interface NamespaceSummary {
  namespace: string;
  principal: string | null;
  retentionDays: number | null;
  entries: number;
  updatedAt: string | null;
}

interface Entry {
  id: string;
  namespace: string;
  key: string;
  content: string;
  source: 'tool' | 'compaction' | 'owner' | 'sync';
  created_at: string;
  updated_at: string;
}

interface Props {
  agentId: string;
}

const PAGE = 20;

/** The namespaces every agent has, whether or not anything is kept in them yet. */
const FIXED_NAMESPACES = ['owner', 'shared'] as const;

/**
 * The server's namespaces plus the fixed ones it did not list (they have no
 * notes and no retention yet), in the server's order: `owner`, `shared`,
 * then the callers.
 */
export function withFixedNamespaces(list: NamespaceSummary[]): NamespaceSummary[] {
  const have = new Set(list.map((n) => n.namespace));
  const missing = FIXED_NAMESPACES.filter((ns) => !have.has(ns)).map(
    (namespace): NamespaceSummary => ({ namespace, principal: null, retentionDays: null, entries: 0, updatedAt: null }),
  );
  const order = (ns: string) => (ns === 'owner' ? 0 : ns === 'shared' ? 1 : 2);
  return [...missing, ...list].sort((a, b) => order(a.namespace) - order(b.namespace));
}

function formatDate(raw: string | null) {
  if (!raw) return '';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function AgentMemoryPanel({ agentId }: Props) {
  const t = useT();
  const [namespaces, setNamespaces] = useState<NamespaceSummary[]>([]);
  const [selected, setSelected] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ namespace: string; key: string; content: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [retention, setRetention] = useState<string>('');
  const [retentionSaving, setRetentionSaving] = useState(false);
  const retentionHintId = useId();

  const base = `/api/agents/${encodeURIComponent(agentId)}/memory`;

  const namespaceLabel = useCallback(
    (ns: NamespaceSummary | string) => {
      const name = typeof ns === 'string' ? ns : ns.namespace;
      if (name === 'owner') return t('agentMemory.ownerNamespace');
      if (name === 'shared') return t('agentMemory.sharedNamespace');
      const principal = typeof ns === 'string' ? name.replace(/^caller:/, '') : (ns.principal ?? name.replace(/^caller:/, ''));
      return t('agentMemory.callerNamespace', { principal });
    },
    [t],
  );

  const loadNamespaces = useCallback(async () => {
    try {
      const res = await fetch(`${base}?view=namespaces`);
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as { namespaces: NamespaceSummary[] };
      setNamespaces(withFixedNamespaces(data.namespaces ?? []));
    } catch {
      setError(t('agentMemory.loadFailed'));
    }
  }, [base, t]);

  const loadEntries = useCallback(
    async (offset: number, append: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
        if (selected !== 'all') qs.set('namespace', selected);
        if (query.trim()) qs.set('q', query.trim());
        const res = await fetch(`${base}?${qs}`);
        if (!res.ok) throw new Error(String(res.status));
        const data = (await res.json()) as { entries: Entry[] };
        const page = data.entries ?? [];
        setEntries((prev) => (append ? [...prev, ...page] : page));
        setMore(!query.trim() && page.length === PAGE);
      } catch {
        setError(t('agentMemory.loadFailed'));
      } finally {
        setLoading(false);
      }
    },
    [base, selected, query, t],
  );

  useEffect(() => {
    void loadNamespaces();
  }, [loadNamespaces]);

  useEffect(() => {
    void loadEntries(0, false);
  }, [loadEntries]);

  useEffect(() => {
    const current = namespaces.find((n) => n.namespace === selected);
    setRetention(current?.retentionDays ? String(current.retentionDays) : '');
  }, [namespaces, selected]);

  const refresh = async () => {
    await loadNamespaces();
    await loadEntries(0, false);
  };

  const forget = async (entry: Entry) => {
    if (!window.confirm(t('agentMemory.forgetConfirm', { key: entry.key }))) return;
    const res = await fetch(`${base}?namespace=${encodeURIComponent(entry.namespace)}&key=${encodeURIComponent(entry.key)}`, { method: 'DELETE' });
    if (res.ok) await refresh();
    else setError(t('agentMemory.saveFailed'));
  };

  const forgetNamespace = async () => {
    if (selected === 'all') return;
    if (!window.confirm(t('agentMemory.forgetNamespaceConfirm', { namespace: namespaceLabel(selected) }))) return;
    const res = await fetch(`${base}?namespace=${encodeURIComponent(selected)}&all=1`, { method: 'DELETE' });
    if (res.ok) await refresh();
    else setError(t('agentMemory.saveFailed'));
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await fetch(base, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ namespace: editing.namespace, key: editing.key, content: editing.content }),
      });
      if (!res.ok) {
        setError(t('agentMemory.saveFailed'));
        return;
      }
      setEditing(null);
      await refresh();
    } finally {
      setSaving(false);
    }
  };

  const saveRetention = async () => {
    if (selected === 'all') return;
    setRetentionSaving(true);
    try {
      const days = retention.trim() === '' ? null : Number(retention);
      const res = await fetch(base, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ namespace: selected, retentionDays: days }),
      });
      if (!res.ok) setError(t('agentMemory.saveFailed'));
      else await loadNamespaces();
    } finally {
      setRetentionSaving(false);
    }
  };

  const exportHref = `${base}?export=1${selected !== 'all' ? `&namespace=${encodeURIComponent(selected)}` : ''}`;
  const total = namespaces.reduce((sum, n) => sum + n.entries, 0);

  return (
    <div className="mt-4 border-t border-border pt-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            {t('agentMemory.title')}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{t('agentMemory.subtitle')}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" onClick={() => void refresh()} className="text-xs text-muted-foreground hover:bg-muted/50 p-1.5 rounded-md transition-colors" title={t('agentMemory.refresh')} aria-label={t('agentMemory.refresh')}>
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <a href={exportHref} download className="text-xs text-primary hover:bg-primary/10 px-2.5 py-1 rounded-md transition-colors flex items-center gap-1">
            <Download className="h-3.5 w-3.5" /> {t('agentMemory.export')}
          </a>
        </div>
      </div>

      <div className="flex flex-col @md/main:flex-row gap-2">
        <select value={selected} onChange={(e) => setSelected(e.target.value)} className="input text-sm @md/main:max-w-[260px]" aria-label={t('agentMemory.namespaceLabel')}>
          <option value="all">{t('agentMemory.allNamespaces', { count: total })}</option>
          {namespaces.map((n) => (
            <option key={n.namespace} value={n.namespace}>
              {namespaceLabel(n)} ({n.entries})
            </option>
          ))}
        </select>
        <div className="relative flex-1">
          <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('agentMemory.searchPlaceholder')} aria-label={t('agentMemory.searchPlaceholder')} className="input w-full text-sm pl-8" />
        </div>
      </div>

      {selected === 'all' ? (
        <p className="text-xs text-muted-foreground" data-testid="memory-retention-pick">{t('agentMemory.retentionPickNamespace')}</p>
      ) : (
        <div className="flex flex-col @md/main:flex-row @md/main:items-center gap-2 rounded-lg border border-border p-3">
          <div className="flex-1">
            <span className="text-sm font-medium">{t('agentMemory.retentionLabel')}</span>
            <p id={retentionHintId} className="text-xs text-muted-foreground">{t('agentMemory.retentionHint')}</p>
          </div>
          <div className="flex items-center gap-2">
            <input type="number" min={1} max={3650} value={retention} onChange={(e) => setRetention(e.target.value)} placeholder={t('agentMemory.retentionForever')} className="input w-28 text-sm" aria-label={t('agentMemory.retentionLabel')} aria-describedby={retentionHintId} />
            <button type="button" onClick={() => void saveRetention()} disabled={retentionSaving} className="text-xs text-primary hover:bg-primary/10 px-2.5 py-1 rounded-md transition-colors">
              {retentionSaving ? t('actions.saving') : t('actions.save')}
            </button>
            <button type="button" onClick={() => void forgetNamespace()} className="text-xs text-destructive hover:bg-destructive/10 px-2.5 py-1 rounded-md transition-colors flex items-center gap-1">
              <Trash2 className="h-3.5 w-3.5" /> {t('agentMemory.forgetNamespace')}
            </button>
          </div>
        </div>
      )}

      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}

      {loading && entries.length === 0 ? (
        <div className="flex items-center justify-center py-6">
          <Spinner size="lg" tone="muted" />
        </div>
      ) : entries.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('agentMemory.none')}</p>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => {
            const isEditing = editing && editing.namespace === entry.namespace && editing.key === entry.key;
            return (
              <div key={`${entry.namespace}/${entry.key}`} className="rounded-lg border border-border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{entry.key}</span>
                      <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{namespaceLabel(entry.namespace)}</span>
                      <span className="text-[10px] text-muted-foreground">{t(`agentMemory.source.${entry.source}`)}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t('agentMemory.updated', { when: formatDate(entry.updated_at) })}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isEditing ? (
                      <button type="button" onClick={() => setEditing(null)} className="text-xs text-muted-foreground hover:bg-muted/50 p-1.5 rounded-md transition-colors" aria-label={t('actions.cancel')}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button type="button" onClick={() => setEditing({ namespace: entry.namespace, key: entry.key, content: entry.content })} className="text-xs text-primary hover:bg-primary/10 p-1.5 rounded-md transition-colors" aria-label={t('actions.edit')} title={t('actions.edit')}>
                        <FileEdit className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button type="button" onClick={() => void forget(entry)} className="text-xs text-destructive hover:bg-destructive/10 p-1.5 rounded-md transition-colors" aria-label={t('agentMemory.forget')} title={t('agentMemory.forget')}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {isEditing ? (
                  <div className="mt-2 space-y-2">
                    <textarea value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className="textarea w-full text-sm" rows={4} />
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setEditing(null)} className="text-xs text-muted-foreground hover:bg-muted/50 px-2.5 py-1 rounded-md transition-colors">
                        {t('actions.cancel')}
                      </button>
                      <button type="button" onClick={() => void saveEdit()} disabled={saving} className="text-xs text-primary hover:bg-primary/10 px-2.5 py-1 rounded-md transition-colors">
                        {saving ? t('actions.saving') : t('actions.save')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm mt-2 whitespace-pre-wrap break-words line-clamp-6">{entry.content}</p>
                )}
              </div>
            );
          })}
          {more && (
            <button type="button" onClick={() => void loadEntries(entries.length, true)} disabled={loading} className="text-xs text-primary hover:bg-primary/10 px-2.5 py-1 rounded-md transition-colors">
              {loading ? t('actions.loading') : t('agentMemory.loadMore')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
